import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getFormattedUrl } from "../../common/utils";
import CButton from "../../components/CButton";
import CSVUploadComponent from "../../components/CSVUploadComponent";
import CsvValidationPanel from "../../components/CsvValidationPanel";
import { addMultipleEmployees } from "../../slices/multiEmployeeSlice";
import { parseUploadedRows } from "./csvFileUtils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;
const MAX_ROWS = 25000;

// ---------------------------------------------------------------------------
// Pure helpers — defined outside the component so they are not re-created
// on every render.
// ---------------------------------------------------------------------------

/**
 * SA cell normalisation — must match 01-prepare-payroll-local.js exactly so
 * duplicate detection treats '0721234567', '+27 72 123 4567', '27721234567'
 * as the same number.
 */
function normalizeMsisdn(m) {
  const digits = String(m || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("27") && digits.length === 11) return digits.slice(2);
  if (digits.startsWith("0") && digits.length === 10) return digits.slice(1);
  return digits;
}

/**
 * Run all row-level checks and classify each row as clean, excluded, or
 * flagged (clean but with warnings).
 *
 * IMPORTANT: XLSX.utils.sheet_to_json uses the EXACT header string from the
 * file as the object key.  The requiredHeaders list defines the exact strings:
 * 'UUID', 'ID Number', 'Emp Number', 'Cell Number', 'Birth Date',
 * 'Employment Date', 'Termination Date', 'EMPLOYER'.
 * Do NOT use camelCase aliases — they are always undefined against real files
 * and silently swallow missing-column bugs.
 */
function validateCsvRows(rows, headers) {
  const cleanRows = [];
  const excludedRows = [];
  const flaggedRows = [];

  // Pass 1: build frequency maps for duplicate detection.
  const uuidCounts = new Map();
  const idNumberCounts = new Map();
  const cellNumberCounts = new Map(); // keyed by normalizeMsisdn result
  const empNumberCounts = new Map();

  for (const r of rows) {
    const uuid = String(r["UUID"] || "").trim();
    const idNum = String(r["ID Number"] || "").trim();
    const cell = normalizeMsisdn(r["Cell Number"]);
    const emp = String(r["Emp Number"] || "").trim();

    if (uuid) uuidCounts.set(uuid, (uuidCounts.get(uuid) || 0) + 1);
    if (idNum) idNumberCounts.set(idNum, (idNumberCounts.get(idNum) || 0) + 1);
    if (cell) cellNumberCounts.set(cell, (cellNumberCounts.get(cell) || 0) + 1);
    if (emp) empNumberCounts.set(emp, (empNumberCounts.get(emp) || 0) + 1);
  }

  // Pass 2: per-row classification.
  rows.forEach((r, idx) => {
    const issues = [];

    // UUID checks (CRITICAL)
    const uuid = String(r["UUID"] || "").trim();
    if (!uuid) {
      issues.push({
        severity: "CRITICAL",
        code: "MISSING_UUID",
        exclude: true,
      });
    } else if (!UUID_RE.test(uuid)) {
      issues.push({
        severity: "CRITICAL",
        code: "INVALID_UUID_FORMAT",
        exclude: true,
      });
    } else if (uuidCounts.get(uuid) > 1) {
      // Mirrors migration script: entire duplicate UUID group is removed from
      // the upload-ready file — not just the second occurrence.
      issues.push({
        severity: "CRITICAL",
        code: "DUPLICATE_UUID_GROUP",
        exclude: true,
      });
    }

    // Required field checks (HIGH, exclude)
    const employer = String(r["EMPLOYER"] || "").trim();
    if (!employer) {
      issues.push({
        severity: "HIGH",
        code: "MISSING_EMPLOYER",
        exclude: true,
      });
    }

    const idNum = String(r["ID Number"] || "").trim();
    if (!idNum) {
      issues.push({
        severity: "HIGH",
        code: "MISSING_ID_NUMBER",
        exclude: true,
      });
    } else if (idNumberCounts.get(idNum) > 1) {
      // Script COLUMNS_TO_CHECK rates ID Number as HIGH but keeps duplicates
      // in the upload-ready file (writes them to duplicates.csv as a warning).
      issues.push({
        severity: "HIGH",
        code: "DUPLICATE_ID_NUMBER",
        exclude: false,
      });
    }

    const emp = String(r["Emp Number"] || "").trim();
    if (!emp) {
      issues.push({
        severity: "HIGH",
        code: "MISSING_EMP_NUMBER",
        exclude: true,
      });
    } else if (empNumberCounts.get(emp) > 1) {
      issues.push({
        severity: "MEDIUM",
        code: "DUPLICATE_EMP_NUMBER",
        exclude: false,
      });
    }

    const cellNorm = normalizeMsisdn(r["Cell Number"]);
    if (!cellNorm) {
      issues.push({
        severity: "HIGH",
        code: "MISSING_CELL_NUMBER",
        exclude: true,
      });
    } else if (cellNumberCounts.get(cellNorm) > 1) {
      issues.push({
        severity: "MEDIUM",
        code: "DUPLICATE_CELL_NUMBER",
        exclude: false,
      });
    }

    // Date format checks — use normalizeDateValue() so Date objects, serial
    // numbers, and plain strings all resolve to a comparable YYYY/MM/DD string.
    for (const dateField of [
      "Birth Date",
      "Employment Date",
      "Termination Date",
    ]) {
      const v = normalizeDateValue(r[dateField]);
      if (v && !DATE_RE.test(v)) {
        issues.push({
          severity: "HIGH",
          code: `INVALID_DATE_FORMAT_${dateField.replace(/ /g, "_").toUpperCase()}`,
          exclude: true,
        });
      }
    }

    const shouldExclude = issues.some((i) => i.exclude);
    // rowIndex is 1-based (+1 for header row, +1 for 0-based index)
    const enriched = { rowIndex: idx + 2, data: r, issues };

    if (shouldExclude) {
      excludedRows.push(enriched);
    } else {
      cleanRows.push(r);
      if (issues.length > 0) {
        flaggedRows.push(enriched);
      }
    }
  });

  return { cleanRows, excludedRows, flaggedRows, headers };
}

/**
 * Normalise a date cell value from XLSX regardless of how xlsx stored it:
 *   - JS Date object (cellDates:true) → format using local time
 *   - Excel serial number (raw:true, no cellDates) → decode via XLSX.SSF
 *   - String → return as-is
 * Always returns a string so the YYYY/MM/DD regex test works correctly.
 */
function normalizeDateValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return "";
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}/${m}/${d}`;
  }
  if (typeof v === "number") {
    // Excel date serial — decode with XLSX built-in SSF
    try {
      const decoded = XLSX.SSF.parse_date_code(v);
      if (decoded) {
        return `${decoded.y}/${String(decoded.m).padStart(2, "0")}/${String(decoded.d).padStart(2, "0")}`;
      }
    } catch (_) {
      // ignore — fall through to string coercion
    }
    return "";
  }
  return String(v).trim();
}

/**
 * Rebuild an Excel file from the clean rows.
 */
function buildCleanFile(originalFile, headers, cleanRows) {
  const ws = XLSX.utils.json_to_sheet(cleanRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new File(
    [buf],
    originalFile.name.replace(/\.xlsx?$/i, "-clean.xlsx"),
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );
}

/**
 * Build a validationResult shaped for the error state.  Stored in the same
 * slot as a successful result so the panel has one source of truth.
 */
function buildErrorResult(message) {
  return {
    error: message,
    cleanRows: [],
    excludedRows: [],
    flaggedRows: [],
    headers: [],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddMultipleEmployees = () => {
  const dispatch = useDispatch();
  const csvUploadRef = useRef(null);

  const requiredHeaders = [
    "EMPLOYER",
    "Emp Number",
    "UUID",
    "Initials",
    "First Name",
    "Last Name",
    "Cell Number",
    "Birth Date",
    "Gender",
    "Race",
    "ID Number",
    "Employment Date",
    "Termination Date",
    "Phys. Line 1",
    "Phys. Line 2",
    "Phys. Line 3",
    "Phys. Postal Code",
    "Phys. Street Number",
    "Phys. Province",
    "Phys. Country",
    "Position",
    "Bank Name",
    "Account Type",
    "Branch Code",
    "Account No",
    "Pay Point",
  ];

  const [fileHeaders, setFileHeaders] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [cleanFile, setCleanFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasServerConflicts, setHasServerConflicts] = useState(false);
  const [identityUpdates, setIdentityUpdates] = useState([]);
  const [identityUpdateDigest, setIdentityUpdateDigest] = useState("");
  const [payrollWriteDigest, setPayrollWriteDigest] = useState("");
  const [identityUpdatesConfirmed, setIdentityUpdatesConfirmed] =
    useState(false);
  const [employerTransfers, setEmployerTransfers] = useState([]);
  const [employerTransferDigest, setEmployerTransferDigest] = useState("");
  const [employerTransferDecisions, setEmployerTransferDecisions] = useState({});
  const [serverPreflightFile, setServerPreflightFile] = useState(null);
  const verifiedEmployerTransferCount = employerTransfers.filter(
    (transfer) => transfer.verified,
  ).length;
  const selectedEmployerTransferRows = employerTransfers
    .filter(
      (transfer) =>
        transfer.verified &&
        employerTransferDecisions[transfer.rowNumber] === "move",
    )
    .map((transfer) => transfer.rowNumber);
  const omittedEmployerTransferCount =
    employerTransfers.length - selectedEmployerTransferRows.length;
  const decidedEmployerTransferCount = employerTransfers.filter(
    (transfer) =>
      !transfer.verified || employerTransferDecisions[transfer.rowNumber],
  ).length;

  const handleFileUploaded = async (file, headers) => {
    // Always reset — covers both the new-file path and the delete-file path
    // (CSVUploadComponent calls onFileUploaded(null, null) on delete).
    setValidationResult(null);
    setCleanFile(null);
    setFileHeaders([]);
    setHasServerConflicts(false);
    setIdentityUpdates([]);
    setIdentityUpdateDigest("");
    setPayrollWriteDigest("");
    setIdentityUpdatesConfirmed(false);
    setEmployerTransfers([]);
    setEmployerTransferDigest("");
    setEmployerTransferDecisions({});
    setServerPreflightFile(null);

    if (!file) {
      setIsValidating(false);
      return;
    }

    setFileHeaders(headers || []);
    setIsValidating(true);

    try {
      const rows = parseUploadedRows(await file.arrayBuffer());

      if (rows.length > MAX_ROWS) {
        setValidationResult(
          buildErrorResult(
            `File too large: ${rows.length.toLocaleString()} rows. Max supported: ${MAX_ROWS.toLocaleString()}. Split the file and re-upload.`,
          ),
        );
        return;
      }

      let result = validateCsvRows(rows, headers || []);
      setValidationResult(result);

      if (result.cleanRows.length > 0) {
        const clientCleanFile = buildCleanFile(
          file,
          headers || [],
          result.cleanRows,
        );
        setServerPreflightFile(clientCleanFile);
        const preflightData = new FormData();
        preflightData.append("csv", clientCleanFile);
        const response = await dispatch(
          addMultipleEmployees({ data: preflightData, validateOnly: true }),
        );

        if (response.error) {
          setValidationResult(
            buildErrorResult(response.payload || "Server preflight failed"),
          );
          return;
        }

        const transfers = response.payload?.data?.employerTransfers || [];
        const transferRowNumbers = new Set(
          transfers.map((transfer) => Number(transfer.rowNumber)),
        );
        const conflicts = response.payload?.data?.conflicts || [];
        const excludedConflicts = conflicts.filter(
          (conflict) => !transferRowNumbers.has(Number(conflict.rowNumber)),
        );
        const verifiedIdentityUpdates = (
          response.payload?.data?.identityUpdates || []
        ).map((update) => {
          const sourceRow =
            result.cleanRows[Number(update.rowNumber) - 2] || {};
          return {
            ...update,
            employeeNumber:
              update.employeeNumber ||
              sourceRow["Emp Number"] ||
              "Not supplied",
            employeeName:
              update.employeeName ||
              [sourceRow["First Name"], sourceRow["Last Name"]]
                .filter(Boolean)
                .join(" ") ||
              "Name not supplied",
            cellNumber:
              update.cellNumber || sourceRow["Cell Number"] || "Not supplied",
            payrollEmployer:
              update.payrollEmployer || sourceRow["EMPLOYER"] || "Not supplied",
          };
        });
        setIdentityUpdates(verifiedIdentityUpdates);
        setIdentityUpdateDigest(
          response.payload?.data?.identityUpdateDigest || "",
        );
        setPayrollWriteDigest(
          response.payload?.data?.payrollWriteDigest || "",
        );
        setEmployerTransfers(transfers);
        setEmployerTransferDigest(
          response.payload?.data?.employerTransferDigest || "",
        );
        const blockedIndexes = new Set(
          conflicts.map((conflict) => Number(conflict.rowNumber) - 2),
        );
        const serverExcludedRows = excludedConflicts.map((conflict) => {
          const cleanIndex = Number(conflict.rowNumber) - 2;
          const originalRow = result.cleanRows[cleanIndex] || {};
          const originalIndex = rows.indexOf(originalRow);
          return {
            rowIndex:
              originalIndex >= 0 ? originalIndex + 2 : conflict.rowNumber,
            data: originalRow,
            existingUuid: conflict.existingUuid || "",
            existingEmployer: conflict.existingEmployer || "",
            conflictFields: conflict.conflictFields || [],
            sameEmployer: conflict.sameEmployer,
            sameEmployeeNumber: conflict.sameEmployeeNumber,
            sameIdNumber: conflict.sameIdNumber,
            novaDecision: conflict.novaDecision || "UNAVAILABLE",
            novaReason: conflict.novaReason || "",
            recommendedAction: conflict.recommendedAction || "",
            issues: [
              {
                severity: "CRITICAL",
                code: conflict.errorCode || "SERVER_VALIDATION_ERROR",
                message: conflict.error,
                exclude: true,
              },
            ],
          };
        });
        const safeRows = result.cleanRows.filter(
          (_, index) => !blockedIndexes.has(index),
        );

        result = {
          ...result,
          cleanRows: safeRows,
          excludedRows: [...result.excludedRows, ...serverExcludedRows],
        };
        setValidationResult(result);
        setHasServerConflicts(serverExcludedRows.length > 0);
        setCleanFile(
          safeRows.length > 0
            ? buildCleanFile(file, headers || [], safeRows)
            : null,
        );
      }
    } catch (err) {
      setValidationResult(
        buildErrorResult(`Could not read file: ${err.message}`),
      );
    } finally {
      setIsValidating(false);
    }
  };

  const validateHeaders = (headers) => {
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header),
    );
    const extraHeaders = headers.filter(
      (header) => !requiredHeaders.includes(header),
    );

    if (missingHeaders.length > 0) {
      return `File has missing headers: ${missingHeaders.join(", ")}`;
    }
    if (extraHeaders.length > 0) {
      return `File contains extra headers: ${extraHeaders.join(", ")}`;
    }
    for (let i = 0; i < headers.length; i++) {
      if (headers[i] !== requiredHeaders[i]) {
        return "Invalid headers. The order of the headers does not match.";
      }
    }
    return null;
  };

  const handleDownload = (resUrl) => {
    if (resUrl) {
      const link = document.createElement("a");
      link.href = getFormattedUrl(resUrl);
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmit = () => {
    if (
      !validationResult ||
      (validationResult.cleanRows.length === 0 &&
        selectedEmployerTransferRows.length === 0) ||
      (selectedEmployerTransferRows.length === 0 && !cleanFile) ||
      (selectedEmployerTransferRows.length > 0 && !serverPreflightFile)
    )
      return;

    const validationError = validateHeaders(fileHeaders);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (
      validationResult.excludedRows.length > 0 ||
      selectedEmployerTransferRows.length > 0
    ) {
      const identityUpdateWarning =
        identityUpdates.length > 0
          ? ` ${identityUpdates.length.toLocaleString()} incorrect UUID links will be corrected in place using Nova-verified UUIDs; employee records and history will be preserved.`
          : " No employee identity records will be changed.";
      const employerTransferWarning = selectedEmployerTransferRows.length
        ? ` ${selectedEmployerTransferRows.length.toLocaleString()} Nova-verified employees will be moved to their payroll employers; their previous employments will be deactivated.`
        : " Cross-employer rows will remain excluded.";

      if (
        !window.confirm(
          `${validationResult.cleanRows.length.toLocaleString()} payroll rows will be submitted; ` +
            `${(
              validationResult.excludedRows.length +
              omittedEmployerTransferCount
            ).toLocaleString()} blocked or kept-current rows will be omitted.` +
            identityUpdateWarning +
            employerTransferWarning +
            " Continue?",
        )
      ) {
        return;
      }
    }

    const fileData = new FormData();
    fileData.append(
      "csv",
      selectedEmployerTransferRows.length > 0 && serverPreflightFile
        ? serverPreflightFile
        : cleanFile,
    );

    dispatch(
      addMultipleEmployees({
        data: fileData,
        partial: true,
        approveIdentityUpdates:
          identityUpdates.length > 0 && identityUpdatesConfirmed,
        identityUpdateDigest,
        payrollWriteDigest,
        approveEmployerTransfers:
          selectedEmployerTransferRows.length > 0,
        employerTransferDigest: selectedEmployerTransferRows.length
          ? employerTransferDigest
          : "",
        employerTransferRows: selectedEmployerTransferRows,
      }),
    ).then((res) => {
      if (!res.error) {
        handleDownload(res.payload.data.url);
        // Resetting via handleDelete triggers handleFileUploaded(null, null)
        // which clears all validation state automatically.
        csvUploadRef.current.handleDelete();
      }
    });
  };

  // Derive submit button label and disabled state
  const submitDisabled =
    isValidating ||
    !validationResult ||
    !!validationResult.error ||
    (validationResult.cleanRows.length === 0 &&
      selectedEmployerTransferRows.length === 0) ||
    (identityUpdates.length > 0 && !identityUpdatesConfirmed) ||
    (verifiedEmployerTransferCount > 0 &&
      decidedEmployerTransferCount < employerTransfers.length);

  const submitLabel = isValidating
    ? "Validating…"
    : validationResult &&
        !validationResult.error &&
        validationResult.cleanRows.length === 0 &&
        selectedEmployerTransferRows.length === 0
      ? "0 rows to submit"
      : identityUpdates.length > 0 && !identityUpdatesConfirmed
        ? "Confirm UUID changes above"
        : verifiedEmployerTransferCount > 0 &&
            decidedEmployerTransferCount < employerTransfers.length
          ? "Choose an employer for each row"
        : validationResult && !validationResult.error
          ? `${hasServerConflicts ? "Submit safe" : "Submit"} ${(
              validationResult.cleanRows.length +
              selectedEmployerTransferRows.length
            ).toLocaleString()} rows`
          : "Submit";

  return (
    <div className="main-content report-content upload-csv-content">
      <h2>Multiple Employees</h2>
      <div className="card-box">
        <CSVUploadComponent
          ref={csvUploadRef}
          onFileUploaded={handleFileUploaded}
        />

        {(isValidating || validationResult) && (
          <CsvValidationPanel
            isValidating={isValidating}
            error={validationResult?.error}
            cleanCount={validationResult?.cleanRows?.length ?? 0}
            excludedRows={validationResult?.excludedRows ?? []}
            flaggedRows={validationResult?.flaggedRows ?? []}
            headers={validationResult?.headers ?? []}
            cleanFile={cleanFile}
            identityUpdates={identityUpdates}
            identityUpdatesConfirmed={identityUpdatesConfirmed}
            onConfirmIdentityUpdates={() => setIdentityUpdatesConfirmed(true)}
            employerTransfers={employerTransfers}
            employerTransferDecisions={employerTransferDecisions}
            onEmployerTransferDecision={(rowNumber, decision) =>
              setEmployerTransferDecisions((current) => ({
                ...current,
                [rowNumber]: decision,
              }))
            }
            onAllEmployerTransferDecisions={(decision) =>
              setEmployerTransferDecisions(
                Object.fromEntries(
                  employerTransfers
                    .filter((transfer) => transfer.verified)
                    .map((transfer) => [transfer.rowNumber, decision]),
                ),
              )
            }
          />
        )}

        <div className="upload-csv-btn">
          <CButton
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {submitLabel}
          </CButton>
        </div>
      </div>
    </div>
  );
};

export default AddMultipleEmployees;
